import * as React from 'react';
import styled from '@emotion/styled';
import { useEditorHandlers } from './@editor';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { alpha } from '@mui/material/styles';
import { debounce } from '../../utils/debounce-and-throttle';
import { useAppContext } from '../../contexts/AppContext';
import CircularProgress from '@mui/material/CircularProgress';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

interface SearchFieldProps {
  disableLiveSearch?: boolean;
}

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.theme_component_search_field_icon_color || theme.palette.text.primary,
  '&:hover': {
    color: theme.theme_component_search_field_icon_hover_color || theme.palette.action.hover,
  },
}));

const SearchInput = styled(Input)(({ theme }) => ({
  display: 'flex',
  width: 'inherit',
  padding: theme.spacing(0.375),
  borderRadius: theme.shape.borderRadius,
  fontFamily: theme.typography.button.fontFamily,
  fontSize: theme.typography.button.fontSize,
  fontWeight: theme.typography.button.fontWeight,
  lineHeight: theme.typography.button.lineHeight,
  letterSpacing: theme.typography.button.letterSpacing,
  color: theme.theme_component_search_field_input_color || theme.palette.text.primary,
  backgroundColor: theme.theme_component_search_field_background_color || alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor:
      theme.theme_component_search_field_hover_background_color || alpha(theme.palette.common.white, 0.25),
  },
}));

export const SearchField = (props: SearchFieldProps) => {
  const { disableLiveSearch } = props;
  const { t } = useTranslation('search_field');
  const router = useRouter();
  const [query, setQuery] = React.useState<string>('');
  const appContext = useAppContext();
  const { isLoading, setKeyword } = appContext.useSearch();

  const submitQuery = React.useMemo(
    () =>
      debounce(async (phrase: string) => {
        setKeyword(phrase);
      }, 1000),
    []
  );

  const handleInputChange = React.useCallback(
    (phrase: string | undefined) => {
      console.log(phrase);
      if (!phrase) {
        return setQuery(phrase as string);
      }

      const k = phrase?.replace(/[^\s\w]/gi, '');

      setQuery(k);

      if (!disableLiveSearch) {
        submitQuery(k);
      }
    },
    [disableLiveSearch]
  );

  const handleEnterOnKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      router.push({
        pathname: '/search',
        search: `?${new URLSearchParams({ q: query }).toString()}`,
      });
    }
  };

  const handleClear = () => {
    setQuery('');

    if (!disableLiveSearch) {
      submitQuery(undefined);
    }
  };

  return (
    <SearchInput
      disableUnderline
      autoFocus
      fullWidth
      disabled={isLoading}
      placeholder={t('input_label', 'Search')}
      inputProps={{ 'aria-label': 'search field' }}
      value={query}
      onChange={(event) => handleInputChange(event.currentTarget.value)}
      onKeyPress={handleEnterOnKeyUp}
      startAdornment={
        <InputAdornment position="start">
          <SearchIconButton onClick={() => router.push({ pathname: '/search' })} size="small" disabled={isLoading}>
            <SearchIcon />
          </SearchIconButton>
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end">
          {isLoading ? (
            <SearchIconButton size="small" disableFocusRipple={true}>
              <CircularProgress size={20} />
            </SearchIconButton>
          ) : (
            <SearchIconButton onClick={handleClear} size="small" disabled={!query}>
              <CloseIcon />
            </SearchIconButton>
          )}
        </InputAdornment>
      }
      {...useEditorHandlers('Search Field')}
    />
  );
};
